import React from 'react'
import { Link } from 'react-router-dom'
import { Popover, Space, Tag, Typography } from 'antd'
import ReactMarkdown from 'react-markdown'
import ReadinessTag from '../InventoryAssistant/ReadinessTag'

const { Text } = Typography

let nf = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
})

// One labelled line. Keeps every cell aligned the same way instead of the
// <b>Label:</b> value <br /> soup this table used to be, and leans on antd's
// secondary text so the labels stay legible in dark mode (hardcoded greys do
// not).
// eslint-disable-next-line react/prop-types
const Field = ({ label, children }) => (
  <div style={{ display: 'flex', gap: '6px', lineHeight: 1.5 }}>
    <Text type="secondary" style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
      {label}
    </Text>
    <span style={{ fontSize: '12px', wordBreak: 'break-word' }}>
      {children}
    </span>
  </div>
)

// eslint-disable-next-line react/prop-types
const Stack = ({ children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
    {children}
  </div>
)

const HYBRID_FUELS = [
  'PETROL-HYBRID',
  'DIESEL-HYBRID',
  'PETROL-PLUG-IN-HYBRID',
  'DIESEL-PLUG-IN-HYBRID'
]

const statusColor = status => {
  const value = String(status || '').toUpperCase()
  if (value.includes('AVAILABLE') && !value.includes('UN')) return 'green'
  if (value.includes('SOLD')) return 'red'
  if (value.includes('RESERVED')) return 'orange'
  return 'default'
}

const carTitle = record =>
  String(
    `${record.year || ''} ${record.make?.name || ''} ${record.model?.name ||
      ''} ${record.trim || ''}`
  )
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase()

export const columns = (
  setShowModal,
  setCurrentCar,
  setShowCarImages,
  setQuickViewId
) => {
  return [
    {
      title: 'Vehicle',
      dataIndex: 'make',
      key: 'make',
      width: 300,
      // eslint-disable-next-line react/display-name
      render: (text, record) => (
        <div style={{ display: 'flex', gap: '10px' }}>
          {record.image ? (
            <Popover
              content={
                <img
                  src={record.image}
                  height={220}
                  width={220}
                  alt="car thumbnail"
                />
              }
              title={carTitle(record)}
              trigger="hover"
            >
              <img
                src={record.image}
                height={72}
                width={72}
                alt="car thumbnail"
                style={{
                  borderRadius: '4px',
                  objectFit: 'cover',
                  flexShrink: 0,
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setCurrentCar(record)
                  setShowCarImages(true)
                }}
              />
            </Popover>
          ) : (
            <div
              style={{
                height: 72,
                width: 72,
                borderRadius: '4px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px dashed rgba(128,128,128,0.4)'
              }}
            >
              <Text type="secondary" style={{ fontSize: '11px' }}>
                No image
              </Text>
            </div>
          )}

          <Stack>
            <div style={{ fontWeight: 600, lineHeight: 1.35 }}>
              {carTitle(record)}
              {record.fuelType && HYBRID_FUELS.includes(record.fuelType.name)
                ? ' HYBRID'
                : ''}
            </div>

            <Text type="secondary" style={{ fontSize: '12px' }}>
              {[
                record.bodyType?.name,
                record.transmission?.name,
                record.driveTrain?.name
              ]
                .filter(Boolean)
                .join(' · ') || '—'}
            </Text>

            <div style={{ marginTop: '4px' }}>
              <Tag color={statusColor(record.status?.name)}>
                {record.status?.name
                  ? record.status.name.toUpperCase()
                  : 'NO STATUS'}
              </Tag>
              <Tag color={record.postedOnSocialMedia ? 'green' : 'red'}>
                {record.postedOnSocialMedia ? 'Posted' : 'Not posted'}
              </Tag>
              {/* What the car still needs before it can go live — from the same service the
                  Telegram bot's go-live gate uses, so the list and the bot never disagree. */}
              <ReadinessTag readiness={record.readiness} />
            </div>

            {record.priceCheckNote ? (
              <div style={{ marginTop: '2px' }}>
                <Popover
                  content={
                    <div
                      style={{
                        maxHeight: '300px',
                        overflowY: 'auto',
                        maxWidth: '400px'
                      }}
                    >
                      <ReactMarkdown>{record.priceCheckNote}</ReactMarkdown>
                    </div>
                  }
                  title="Price Check Note"
                  trigger="hover"
                >
                  <Tag color="orange" style={{ cursor: 'pointer' }}>
                    Price check note
                  </Tag>
                </Popover>
              </div>
            ) : null}
          </Stack>
        </div>
      )
    },
    {
      title: 'Identity',
      dataIndex: 'chassisNo',
      key: 'chassisNo',
      width: 200,
      // eslint-disable-next-line react/display-name
      render: (text, record) => (
        <Stack>
          <Field label="Chassis">
            {record.chassisNo ? record.chassisNo.toUpperCase() : 'N/A'}
          </Field>
          <Field label="Origin">
            {record.origin?.country
              ? `${record.origin.country}${
                  record.condition?.name ? ` (${record.condition.name})` : ''
                }`
              : 'N/A'}
          </Field>
          <Field label="Engine">
            {record.engineSize
              ? `${record.engineSize}L${
                  record.fuelType ? ` · ${record.fuelType.name}` : ''
                }`
              : 'N/A'}
          </Field>
          <Field label="Mileage">
            {record.mileage
              ? `${nf.format(record.mileage)} ${record.mileageUnit || ''}`
              : 'N/A'}
          </Field>
        </Stack>
      )
    },
    {
      title: 'Cost',
      dataIndex: 'sourcePrice',
      key: 'sourcePrice',
      width: 180,
      // eslint-disable-next-line react/display-name
      render: (text, record) => (
        <Stack>
          <Field label="Source">
            {record.sourceCurrency
              ? `${record.sourceCurrency.symbol} ${nf.format(
                  record.sourcePrice
                )}`
              : 'N/A'}
            {Number(record.additionalCharge) > 0 &&
            record.additionalChargeCurrency
              ? ` (+${record.additionalChargeCurrency.symbol} ${nf.format(
                  record.additionalCharge
                )})`
              : ''}
          </Field>
          <Field label="MSRP">{record.msrp ? `$ ${record.msrp}` : 'N/A'}</Field>
          <Field label="Duty">
            {record.totalEstimatedDuty
              ? `${record.profitCurrency?.symbol || 'GH₵'} ${nf.format(
                  record.totalEstimatedDuty.total
                )}`
              : 'N/A'}
          </Field>
          <Field label="Profit">
            {record.profitCurrency
              ? `${record.profitCurrency.symbol} ${nf.format(record.profit)} (${
                  record.profitPercentage
                }%)`
              : 'N/A'}
          </Field>
        </Stack>
      )
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: 200,
      // eslint-disable-next-line react/display-name
      render: (text, record) => (
        <Stack>
          <Field label="Selling">
            {record.price && record.profitCurrency && Number(record.profit) > 0
              ? `US$${nf.format(record.priceUsd)} (GH₵ ${nf.format(
                  record.price
                )})`
              : 'N/A'}
          </Field>
          <Field label="Discount">
            {record.discountCurrency && record.discount
              ? `${record.discountCurrency.symbol} ${nf.format(
                  record.discount
                )}`
              : 'N/A'}
          </Field>
          {/* The all-in figure the public site shows. Given its own emphasis
              because it is the only price a customer ever sees now that duty is
              no longer broken out to them. */}
          <div style={{ marginTop: '6px' }}>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              ALL-IN (ON SITE)
            </Text>
            <div style={{ fontWeight: 700, fontSize: '14px' }}>
              {record.landedPriceGhs
                ? `GH₵ ${nf.format(record.landedPriceGhs)}`
                : 'N/A'}
            </div>
          </div>
        </Stack>
      )
    },
    {
      title: 'Saved',
      dataIndex: 'savedByUser',
      key: 'savedByUser',
      width: 70
    },
    {
      title: 'Links',
      dataIndex: 'sourceUrl',
      key: 'sourceUrl',
      width: 130,
      // eslint-disable-next-line react/display-name
      render: (text, record) => (
        <Stack>
          {record.sourceUrl ? (
            <a href={record.sourceUrl} target="blank">
              Source
            </a>
          ) : (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              No source
            </Text>
          )}
          {record.carfaxUrl ? (
            <a href={record.carfaxUrl} target="blank">
              Carfax
            </a>
          ) : null}
          {record.carfaxPdfUrl ? (
            <a href={record.carfaxPdfUrl} target="blank">
              Carfax PDF
            </a>
          ) : null}
          <a
            href={`https://wabenzyautos.com/cars/${record.slug}`}
            target="blank"
          >
            On website
          </a>
        </Stack>
      )
    },
    {
      title: 'Dates',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 190,
      // eslint-disable-next-line react/display-name
      render: (text, record) => {
        const fmt = value =>
          value
            ? `${new Date(value).toDateString()} · ${new Date(
                value
              ).toLocaleTimeString()}`
            : 'N/A'
        return (
          <Stack>
            <Field label="Added">{fmt(record.createdAt)}</Field>
            <Field label="Updated">{fmt(record.updatedAt)}</Field>
            <Field label="Checked">{fmt(record.statusCheckTimestamp)}</Field>
          </Stack>
        )
      }
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 130,
      // eslint-disable-next-line react/display-name
      render: (_, record) => (
        <Space size="middle">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Link
              to={'#'}
              onClick={e => {
                e.preventDefault()
                setQuickViewId(record.id)
              }}
            >
              Quick view
            </Link>
            <Link
              to={`/cars/${record.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in new tab
            </Link>
            <Link
              to={'#'}
              onClick={e => {
                e.preventDefault()
                setCurrentCar(record)
                setShowCarImages(true)
              }}
              className="text-success"
            >
              View Images
            </Link>
            <Link
              to={'#'}
              onClick={e => {
                e.preventDefault()
                setCurrentCar(record)
                setShowModal(true)
              }}
            >
              Update
            </Link>
          </div>
        </Space>
      )
    }
  ]
}

export const imageColumns = (
  setCurrentImage,
  setShowDeleteImageModal,
  setShowImageUpdateModal,
  setShowImageCropModal,
  setShowImageEditModal
) => [
  {
    title: 'Image',
    dataIndex: 'image',
    key: 'image',
    width: 100,
    // eslint-disable-next-line react/display-name
    render: (text, record) => (
      <Space size="middle">
        {/* eslint-disable-next-line */}
        {record.isVideo ? (
          <Popover
            content={
              <div>
                <iframe
                  width="350"
                  height="200"
                  src={`https://www.youtube.com/embed/${record.videoId}`}
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            }
            title="Video"
            trigger="hover"
          >
            <span style={{ cursor: 'pointer' }}>
              Video ID: {record.videoId}
            </span>
          </Popover>
        ) : (
          <Popover
            content={
              <div>
                <img
                  src={record.image}
                  height={200}
                  width={200}
                  alt="car thumbnail"
                />
              </div>
            }
            title="Image"
            trigger="hover"
          >
            <img
              src={record.image}
              height={80}
              width={80}
              alt="car thumbnail"
            />
          </Popover>
        )}
      </Space>
    )
  },
  {
    title: 'Status',
    dataIndex: 'active',
    key: 'active',
    width: 80,
    // eslint-disable-next-line react/display-name
    render: (text, record) => (
      <Space size="middle">
        {/* eslint-disable-next-line */}
        {<span>{record.active ? 'Active' : 'Not Active'}</span>}
      </Space>
    )
  },
  {
    title: 'Showcase Image',
    dataIndex: 'isShowcaseImage',
    key: 'isShowcaseImage',
    width: 80,
    // eslint-disable-next-line react/display-name
    render: (text, record) => (
      <Space size="middle">
        {/* eslint-disable-next-line */}
        {<span>{record.isShowcaseImage ? 'Yes' : 'No'}</span>}
      </Space>
    )
  },
  // {
  //   title: 'Date Created',
  //   dataIndex: 'createdAt',
  //   key: 'createdAt',
  //   width: 120,
  //   // eslint-disable-next-line react/display-name
  //   render: (text, record) => (
  //     <Space size="middle">
  //       {/* eslint-disable-next-line */}
  //       {<span>{new Date(record.createdAt).toDateString()}</span>}
  //     </Space>
  //   )
  // },
  {
    title: 'Action',
    key: 'action',
    fixed: 'right',
    width: 160,
    // eslint-disable-next-line react/display-name
    render: (text, record) => (
      <Space size="middle">
        <span
          onClick={() => {
            setCurrentImage(record)
            setShowImageUpdateModal(true)
          }}
          style={{ cursor: 'pointer' }}
          className="text-primary"
        >
          Update
        </span>
        <span
          onClick={() => {
            setCurrentImage(record)
            setShowImageCropModal(true)
          }}
          style={{ cursor: 'pointer' }}
          className="text-primary"
        >
          Crop Image
        </span>
        <span
          onClick={() => {
            setCurrentImage(record)
            setShowImageEditModal(true)
          }}
          style={{ cursor: 'pointer' }}
          className="text-primary"
        >
          Edit
        </span>
        <span
          onClick={() => {
            setCurrentImage(record)
            setShowDeleteImageModal(true)
          }}
          style={{ color: 'red', cursor: 'pointer' }}
        >
          DELETE
        </span>
      </Space>
    )
  }
]
